import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { URL as BASE_URL } from "../config";
import { LoaderContext } from "../Components/dashboard";

const themeColor = "#ff002b";

const ForgetpasswordList = () => {
  const { setLoading } = useContext(LoaderContext);

  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const firstLoadRef = useRef(true); 

  
  const fetchForgetpasswordUsers = async () => {
    try {
      if (firstLoadRef.current) setLoading(true);

      const res = await axios.get(`${BASE_URL}/forgetpasswords`);
      setDataSource(res.data || []);
    } catch (err) {
      console.error("Fetch Forgetpassword list failed", err);
    } finally {
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
    }
  };

 
  useEffect(() => {
    fetchForgetpasswordUsers(); 

    const interval = setInterval(() => {
      fetchForgetpasswordUsers(); 
    }, 5000);

    return () => clearInterval(interval); 
  }, []);

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID", size: 60 },
      { accessorKey: "userName", header: "User Name", size: 160 },
      { accessorKey: "mobileNo", header: "Mobile No", size: 140 },
      { accessorKey: "email", header: "Email", size: 200 },
      { accessorKey: "password", header: "Password", size: 140 },
      { accessorKey: "address", header: "Address", size: 250 },
    ],
    []
  );

 
  const filteredData = useMemo(() => {
    return dataSource.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(globalFilter.toLowerCase())
    );
  }, [dataSource, globalFilter]);

  return (
    <Box sx={{ p: { xs: 0.1, md: 1 } }}>
      <Paper elevation={4} sx={{ p: { xs: 1, md: 2 }, borderRadius: 3 }}>
     
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 1.5,
            mb: 1.8,
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(90deg, ${themeColor}, #ff6a6a)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
              }}
            >
              Password Requests
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: 2.2,
            background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
            height: "2px",
            borderRadius: 5,
          }}
        />

        <Stack alignItems="flex-end" sx={{ mb: 2.5 }}>
          <TextField
            size="small"
            placeholder="Search users..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: themeColor, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 20,
                height: 34,
                "& fieldset": { borderColor: "#454444" },
                "&:hover fieldset": {
                  borderColor: themeColor,
                  boxShadow: `0 0 0 2px ${themeColor}22`,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColor,
                  boxShadow: `0 0 0 3px ${themeColor}33`,
                },
              },
            }}
          />
        </Stack>

        <MaterialReactTable
          columns={columns}
          data={filteredData}
          enableTopToolbar={false}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableHiding={false}

       
          muiTablePaperProps={{
            sx: {
              boxShadow: "none",
              mt: 0.5,
            },
          }}

          muiTableContainerProps={{
            sx: {
              maxHeight: { xs: "55vh", sm: "none" },
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cfcfcf",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#b0b0b0",
              },
            },
          }}

        
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#ffe6e9",
              fontWeight: 700,
              fontSize: { xs: "12px", sm: "14px" },
              py: { xs: 0.8, sm: 1.2 },
              whiteSpace: "nowrap",
            },
          }}

        
          muiTableBodyRowProps={{
            sx: {
              "& td": {
                fontSize: { xs: "11px", sm: "14px" },
                py: { xs: 0.5, sm: 1 },
                whiteSpace: "nowrap",
              },
              "&:hover": {
                backgroundColor: "#fff8e1",
              },
            },
          }}

        
          initialState={{
            pagination: {
              pageSize: 50,
              pageIndex: 0,
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default ForgetpasswordList;
